import React, { useState, useEffect } from 'react';
import {
  Container, Card, Table, Button, Modal, Form, Row, Col,
  Spinner, Alert, InputGroup, Badge,
} from 'react-bootstrap';
import {
  PlusIcon, PencilSquareIcon, TrashIcon,
  MagnifyingGlassIcon, XMarkIcon, FireIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { useCompanyStarters } from './hooks/useCompanyStarters';
import { companyAPI } from '../../services/api';

const thStyle: React.CSSProperties = {
  color: '#475569', fontWeight: 600, fontSize: '0.8rem',
  textTransform: 'uppercase', letterSpacing: '0.5px', padding: '12px 16px',
};

const CompanyStarters: React.FC = () => {
  const [flourIngredients, setFlourIngredients] = useState<any[]>([]);

  useEffect(() => {
    companyAPI.getIngredients().then(res => {
      const data = Array.isArray(res.data) ? res.data : [];
      setFlourIngredients(data.filter((i: any) => i.category === 'flour'));
    }).catch(() => {});
  }, []);

  const {
    isExpired, loading, search, setSearch, filtered, starters,
    showModal, editingStarter, form, setForm, schedules, saving,
    feedback, setFeedback, deleteState,
    handleOpenCreate, handleOpenEdit, addSchedule, removeSchedule, updateSchedule,
    handleSave, handleConfirmDelete, handleDelete, closeModal, closeDeleteModal,
  } = useCompanyStarters(flourIngredients);

  if (loading) {
    return (
      <CompanyLayout>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="success" />
          <p className="mt-2 text-muted">Loading starters...</p>
        </Container>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <Container fluid className="py-4 px-3 px-md-4 page-content-padding">
        {feedback.success && (
          <Alert variant="success" dismissible onClose={() => setFeedback(p => ({ ...p, success: '' }))} className="mb-3">
            {feedback.success}
          </Alert>
        )}
        {feedback.error && !showModal && (
          <Alert variant="danger" dismissible onClose={() => setFeedback(p => ({ ...p, error: '' }))} className="mb-3">
            {feedback.error}
          </Alert>
        )}

        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-2 mb-2 mb-md-0">
            <div style={{ backgroundColor: '#ecfdf5', borderRadius: '8px', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FireIcon style={{ width: '22px', height: '22px', color: '#059669' }} />
            </div>
            <h4 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>Starters</h4>
            <Badge bg="" style={{ backgroundColor: '#ecfdf5', color: '#059669', fontSize: '0.75rem', fontWeight: '600', padding: '4px 10px', borderRadius: '6px' }}>
              {starters.length}
            </Badge>
          </div>
          <Button variant="success" size="sm" className="d-flex align-items-center gap-1"
            onClick={handleOpenCreate} disabled={isExpired} style={{ padding: '6px 14px' }}>
            <PlusIcon style={{ width: '15px', height: '15px' }} />
            Add Starter
          </Button>
        </div>

        {/* Search */}
        <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '10px' }}>
          <Card.Body className="py-3">
            <InputGroup size="sm" style={{ maxWidth: '400px' }}>
              <InputGroup.Text style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px 0 0 6px' }}>
                <MagnifyingGlassIcon style={{ width: '15px', height: '15px', color: '#94a3b8' }} />
              </InputGroup.Text>
              <Form.Control placeholder="Search starters..." value={search} onChange={e => setSearch(e.target.value)}
                style={{ border: '1px solid #e2e8f0', borderRadius: '0 6px 6px 0' }} />
              {search && (
                <Button variant="outline-secondary" size="sm" onClick={() => setSearch('')} style={{ borderRadius: '6px' }}>
                  <XMarkIcon style={{ width: '14px', height: '14px' }} />
                </Button>
              )}
            </InputGroup>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card className="border-0 shadow-sm" style={{ borderRadius: '10px' }}>
          <div className="table-responsive">
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle} className="d-none d-md-table-cell">Consistency</th>
                  <th style={thStyle} className="d-none d-md-table-cell">Ready By</th>
                  <th style={thStyle} className="d-none d-lg-table-cell">Ratio</th>
                  <th style={thStyle} className="d-none d-lg-table-cell">Flour Source</th>
                  <th style={thStyle} className="d-none d-md-table-cell">Schedules</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      <FireIcon style={{ width: '32px', height: '32px', opacity: 0.3, marginBottom: '8px' }} />
                      <div>{search ? 'No starters match your search.' : 'No starters yet. Add your first starter.'}</div>
                    </td>
                  </tr>
                ) : (
                  filtered.map(s => (
                    <tr key={s.id}>
                      <td style={{ padding: '12px 16px', fontWeight: 500, color: '#1e293b' }}>{s.name}</td>
                      <td style={{ padding: '12px 16px' }} className="d-none d-md-table-cell">
                        <Badge bg="" style={{ backgroundColor: s.consistency === 'liquid' ? '#dbeafe' : '#fef9c3', color: s.consistency === 'liquid' ? '#1d4ed8' : '#854d0e', fontWeight: 500 }}>
                          {s.consistency_display}
                        </Badge>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-md-table-cell">
                        {s.ready_by_time ? s.ready_by_time.slice(0, 5) : '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b', fontFamily: 'monospace' }} className="d-none d-lg-table-cell">
                        {s.maintenance_ratio || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-lg-table-cell">
                        {s.flour_source_name || '—'}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b' }} className="d-none d-md-table-cell">
                        {s.feeding_schedules.length > 0
                          ? <Badge bg="" style={{ backgroundColor: '#f1f5f9', color: '#475569' }}>{s.feeding_schedules.length}</Badge>
                          : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div className="d-flex gap-1">
                          <Button variant="outline-secondary" size="sm" onClick={() => handleOpenEdit(s)} style={{ padding: '4px 8px' }} title="Edit">
                            <PencilSquareIcon style={{ width: '14px', height: '14px' }} />
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleConfirmDelete(s)} style={{ padding: '4px 8px' }} title="Delete">
                            <TrashIcon style={{ width: '14px', height: '14px' }} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      </Container>

      {/* Add / Edit Modal */}
      <Modal show={showModal} onHide={closeModal} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1e293b' }}>
              {editingStarter ? 'Edit Starter' : 'Add New Starter'}
            </Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          {feedback.error && <Alert variant="danger" className="mb-3">{feedback.error}</Alert>}

          <Row className="g-3 mb-3">
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Name <span className="text-danger">*</span></Form.Label>
                <Form.Control size="sm" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Wheat Starter, Rye Starter" />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Consistency <span className="text-danger">*</span></Form.Label>
                <Form.Select size="sm" value={form.consistency} onChange={e => setForm(p => ({ ...p, consistency: e.target.value }))}>
                  <option value="liquid">Liquid</option>
                  <option value="stiff">Stiff</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Ready By Time</Form.Label>
                <Form.Control size="sm" type="time" value={form.ready_by_time} onChange={e => setForm(p => ({ ...p, ready_by_time: e.target.value }))} />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Maintenance Ratio</Form.Label>
                <Form.Control size="sm" value={form.maintenance_ratio} onChange={e => setForm(p => ({ ...p, maintenance_ratio: e.target.value }))} placeholder="e.g. 1:1:1, 1:2:2" />
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Flour Source</Form.Label>
                <Form.Select size="sm" value={form.flour_source} onChange={e => setForm(p => ({ ...p, flour_source: e.target.value }))}>
                  <option value="">No flour selected</option>
                  {flourIngredients.map((i: any) => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151' }}>Notes</Form.Label>
                <Form.Control as="textarea" rows={2} size="sm" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes about this starter..." />
              </Form.Group>
            </Col>
          </Row>

          {/* Feeding Schedules */}
          <hr className="my-3" style={{ borderColor: '#e2e8f0' }} />
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>Feeding Schedules</div>
            <Button variant="success" size="sm" onClick={addSchedule} style={{ padding: '5px 14px', fontSize: '0.8rem' }}>
              <PlusIcon style={{ width: '13px', height: '13px', marginRight: '4px' }} />
              Add Schedule
            </Button>
          </div>
          {schedules.length === 0 ? (
            <p className="text-muted text-center py-2" style={{ fontSize: '0.85rem', backgroundColor: '#f8fafc', borderRadius: '8px' }}>
              No feeding schedules added yet.<br />Click "Add Schedule" to create your first feeding schedule.
            </p>
          ) : (
            <div className="d-flex flex-column gap-2">
              {schedules.map((schedule, idx) => (
                <div key={idx} className="d-flex gap-2 align-items-center p-2 rounded" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                  <Form.Control size="sm" type="time" value={schedule.time} onChange={e => updateSchedule(idx, 'time', e.target.value)} style={{ maxWidth: '110px' }} />
                  <Form.Control size="sm" value={schedule.ratio} onChange={e => updateSchedule(idx, 'ratio', e.target.value)} placeholder="Ratio (e.g. 1:2:2)" style={{ maxWidth: '140px' }} />
                  <Form.Control size="sm" value={schedule.notes} onChange={e => updateSchedule(idx, 'notes', e.target.value)} placeholder="Notes..." />
                  <Button variant="link" className="p-0 text-danger flex-shrink-0" onClick={() => removeSchedule(idx)} title="Remove">
                    <XMarkIcon style={{ width: '16px', height: '16px' }} />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: '1px solid #e2e8f0' }}>
          <Button variant="outline-secondary" size="sm" onClick={closeModal} disabled={saving}>Cancel</Button>
          <Button variant="success" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? <><Spinner size="sm" animation="border" className="me-1" />Saving...</> : (editingStarter ? 'Save Changes' : 'Create')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={deleteState.showModal} onHide={closeDeleteModal} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1rem', fontWeight: 600 }}>Delete Starter</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">Delete <strong>{deleteState.starter?.name}</strong>? This cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" size="sm" onClick={closeDeleteModal} disabled={deleteState.deleting}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={handleDelete} disabled={deleteState.deleting}>
            {deleteState.deleting ? <><Spinner size="sm" animation="border" className="me-1" />Deleting...</> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </CompanyLayout>
  );
};

export default CompanyStarters;
